import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Autocomplete,
  Typography,
} from '@mui/material';
import { Task, Contact, Initiative } from '../../types/database';
import { useAuth } from '../../contexts/AuthContext';
import { useDatabase } from '../../hooks/useDatabase';

interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (task: Omit<Task, 'id' | 'created_at' | 'updated_at'>) => void;
  task?: Task | null;
  prefillInitiative?: string; // Initiative ID to pre-fill when creating a new task
}

export const TaskModal: React.FC<TaskModalProps> = ({
  open,
  onClose,
  onSubmit,
  task,
}) => {
  const { user } = useAuth();
  const db = useDatabase();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '' as string | undefined,
    status: 'pending' as 'pending' | 'in_progress' | 'completed' | 'cancelled',
    priority: 'medium' as 'low' | 'medium' | 'high',
    entity_type: 'none' as 'contact' | 'initiative' | 'none',
    entity_id: '',
    assignee: 'Me' as 'Me' | 'MyCatalyze',
    tags: [] as string[],
    time_tag: '' as 'today' | 'tomorrow' | 'this_week' | 'next_week' | 'next_month' | 'later' | '',
  });

  // Load contacts and initiatives when modal opens
  useEffect(() => {
    if (open) {
      const loadData = async () => {
        setLoading(true);
        try {
          const [contactsResponse, initiativesResponse] = await Promise.all([
            db.getContacts(),
            db.getInitiatives(),
          ]);
          
          if (contactsResponse.data) setContacts(contactsResponse.data);
          if (initiativesResponse.data) setInitiatives(initiativesResponse.data);
        } catch (error) {
          console.error('Failed to load contacts/initiatives:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    }
  }, [open, db]);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        due_date: task.due_date,
        status: task.status,
        priority: task.priority,
        entity_type: task.entity_type || 'none',
        entity_id: task.entity_id || '',
        assignee: task.assignee || 'Me',
        tags: task.tags || [],
        time_tag: task.time_tag || '',
      });
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: '',
        status: 'pending',
        priority: 'medium',
        entity_type: 'none',
        entity_id: '',
        assignee: 'Me',
        tags: [],
        time_tag: '',
      });
    }
  }, [task]);

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear entity_id when entity_type changes
      ...(name === 'entity_type' && { entity_id: '' })
    }));
  };

  // Helper functions to get selected entity
  const getSelectedContact = () => {
    if (formData.entity_type === 'contact' && formData.entity_id) {
      return contacts.find(c => c.id === formData.entity_id);
    }
    return null;
  };

  const getSelectedInitiative = () => {
    if (formData.entity_type === 'initiative' && formData.entity_id) {
      return initiatives.find(i => i.id === formData.entity_id);
    }
    return null;
  };

  const handleSubmit = () => {
    if (!user?.id) {
      return;
    }
    const taskData = {
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      due_date: formData.due_date || undefined,
      status: formData.status,
      priority: formData.priority,
      entity_type: formData.entity_type === 'none' ? undefined : formData.entity_type,
      entity_id: formData.entity_id || undefined,
      assignee: formData.assignee,
      tags: formData.tags,
      time_tag: formData.time_tag || undefined,
      user_id: user.id,
    };
    onSubmit(taskData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {task ? 'Edit Task' : 'Add New Task'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleFormChange}
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              label="Priority"
              onChange={handleSelectChange}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assignee</InputLabel>
            <Select
              name="assignee"
              value={formData.assignee}
              label="Assignee"
              onChange={handleSelectChange}
            >
              <MenuItem value="Me">Me</MenuItem>
              <MenuItem value="MyCatalyze">MyCatalyze</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Time Tag</InputLabel>
            <Select
              name="time_tag"
              value={formData.time_tag}
              label="Time Tag"
              onChange={handleSelectChange}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="tomorrow">Tomorrow</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="next_week">Next Week</MenuItem>
              <MenuItem value="next_month">Next Month</MenuItem>
              <MenuItem value="later">Later</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              label="Status"
              onChange={handleSelectChange}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Related Entity Type</InputLabel>
              <Select
                name="entity_type"
                value={formData.entity_type}
                label="Related Entity Type"
                onChange={handleSelectChange}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="contact">Contact</MenuItem>
                <MenuItem value="initiative">Initiative</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              sx={{ flex: 1 }}
              label="Due Date (Optional)"
              name="due_date"
              type="datetime-local"
              value={formData.due_date}
              onChange={handleFormChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
          
          {formData.entity_type === 'contact' && (
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => option.name || 'Unnamed'}
              value={getSelectedContact()}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  entity_id: newValue?.id || ''
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contact"
                  placeholder="Search contacts..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name || 'Unnamed'}
                    </Typography>
                  </Box>
                </Box>
              )}
              loading={loading}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  (option.name?.toLowerCase() || '').includes(searchTerm)
                );
              }}
            />
          )}

          {formData.entity_type === 'initiative' && (
            <Autocomplete
              options={initiatives}
              getOptionLabel={(option) => `${option.name || 'Unnamed'} (${option.status || 'No status'})`}
              value={getSelectedInitiative()}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  entity_id: newValue?.id || ''
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Initiative"
                  placeholder="Search initiatives..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.name || 'Unnamed'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.status || 'No status'} • {option.description || 'No description'}
                    </Typography>
                  </Box>
                </Box>
              )}
              loading={loading}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option =>
                  (option.name?.toLowerCase() || '').includes(searchTerm) ||
                  (option.description?.toLowerCase() || '').includes(searchTerm) ||
                  (option.status?.toLowerCase() || '').includes(searchTerm)
                );
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!formData.title || !user?.id}
        >
          {task ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 